import type { FriendlyKeyValueUrl, KeyValue, PageEnvelope } from "@skv/core";
import {
  EventBadge,
  EventCarousel,
  ResultMetaList,
  ResultMetaListItem,
} from "@skv/core";
import { getDateRange, resizeImg } from "@skv/util";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";

type EventsViewModel = {
  events: EventData[];
  oneOffOpportunitiesUrl: string;
  eventOpportunitiesUrl: string;
  states: FriendlyKeyValueUrl[];
};

type EventData = {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  location: string;
  friendlyName: string;
  url: string;
  organisationName: string;
  organisationLogoUrl: string | null;
};

interface EventDiscoverySliderProps {
  country: "Australia" | "New Zealand";
}

async function fetchCarouselData(stateId?: number) {
  const location = stateId ? `stateId=${stateId}&` : "";
  const date = format(new Date(), "yyyy-MM-dd");
  return await fetch(`/volunteering/events?${location}date=${date}`)
    .then<PageEnvelope<EventsViewModel>>((res) => {
      if (res.ok) {
        return res.json();
      }
      throw new Error("Server Error");
    })
    .then(({ data }) => data);
}

export const EventDiscoverySlider = (props: EventDiscoverySliderProps) => {
  const [selectedState, setSelectedState] = useState<FriendlyKeyValueUrl>();
  const [data, setData] = useState<EventsViewModel>({
    events: [],
    oneOffOpportunitiesUrl: "",
    eventOpportunitiesUrl: "",
    states: [],
  });

  useEffect(() => {
    const getData = async () => {
      const res = await fetchCarouselData(selectedState?.id);
      setData(res);
    };

    getData();
  }, [selectedState]);

  const handleLocationChange = async (stateId?: number) => {
    if (stateId !== selectedState?.id) {
      setSelectedState(() => data.states.find((s) => s.id === stateId));
    }
  };

  if (data.events.length < 4 && !selectedState) {
    // Less than 4 events country wide => hide the carousel
    return null;
  }

  return (
    <>
      <section className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h2 className="h3">Get started</h2>
          <span>Find an event in your local community</span>
        </div>
        <fieldset className="d-flex justify-content-stretch align-self-stretch align-items-center gap-3 bg-gray-100 rounded pl-3 pr-2 py-2 ml-md-auto">
          <label
            htmlFor="event-location"
            className="form-label font-weight-bold mb-0"
          >
            Location
          </label>

          <LocationDropdown
            {...props}
            selectedState={selectedState}
            onChange={handleLocationChange}
            stateList={data.states || []}
          />
        </fieldset>
      </section>
      {data.events.length > 0 ? (
        <section className="mb-6">
          <EventCarousel itemsCount={data.events.length}>
            {data.events.map((item, idx) => (
              <CarouselItem key={idx} item={item} />
            ))}
          </EventCarousel>
          <div className="text-right mt-2">
            <a
              href={data.eventOpportunitiesUrl}
              className="btn btn-forward text-nowrap border-0"
            >
              Browse all<span className="sr-only">events</span>
            </a>
          </div>
        </section>
      ) : (
        selectedState && (
          <NoEventsInState
            state={selectedState}
            oneOffOpportunitiesUrl={data.oneOffOpportunitiesUrl}
          />
        )
      )}
    </>
  );
};

const CarouselItem = ({ item }: { item: EventData }) => {
  return (
    <div className="slide">
      <div
        className="card m-2"
        css={`
          border: 2px solid #eeeeee;
          &.card:hover {
            text-decoration: none;
            background-color: var(--light);
          }
        `}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-body">
          <h4 className="fs-6 mb-1">
            <a className="stretched-link text-reset" href={item.url}>
              {item.name}
            </a>
          </h4>
          <span className="fs-7">{item.organisationName}</span>
          <div className="my-2">
            <EventBadge date={item.startDate} />
          </div>
          <div className="d-flex flex-column flex-grow-1">
            <ResultMetaList className="d-flex flex-column">
              <ResultMetaListItem className="text-truncate" icon="marker">
                {item.location}
              </ResultMetaListItem>
              {item.startDate && (
                <ResultMetaListItem icon="calendar">
                  {getDateRange(item.startDate, item.endDate)}
                </ResultMetaListItem>
              )}
            </ResultMetaList>
          </div>
        </div>
        {item.organisationLogoUrl && (
          <div className="card-footer bg-transparent border-0 px-3 pb-3">
            <img
              className="img-fluid"
              srcSet={`${resizeImg(item.organisationLogoUrl, "180x120")} 2x`}
              src={resizeImg(item.organisationLogoUrl, "90x60")}
              alt={`${item.organisationName} logo`}
              width={90}
              height={60}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface NoEventsInStateProps {
  state: FriendlyKeyValueUrl;
  oneOffOpportunitiesUrl: string;
}

const NoEventsInState = (props: NoEventsInStateProps) => {
  return (
    <div className="mb-6">
      <h3 className="h5">No upcoming events in the {props.state.name}</h3>
      <p>
        Sorry, there are no upcoming events listed at the moment.
        <br />
        Maybe try one of the following...
      </p>
      <ul>
        <li>
          <a href={props.oneOffOpportunitiesUrl}>
            Browse other one off opportunities
          </a>
        </li>
        <li>
          <a href={props.state.url}>
            Browse all opportunities in {props.state.name}
          </a>
        </li>
      </ul>
    </div>
  );
};

interface LocationDropdownProps extends EventDiscoverySliderProps {
  selectedState: KeyValue | undefined;
  onChange: (stateId?: number) => void;
  stateList: FriendlyKeyValueUrl[];
}

const LocationDropdown = (props: LocationDropdownProps) => {
  return (
    <Dropdown
      className="flex-grow-1"
      css={`
        min-width: 240px;
      `}
    >
      <Dropdown.Toggle
        id="location-dropdown"
        className="w-100 bg-white font-weight-normal text-dark border-0 d-flex justify-content-between align-items-center"
      >
        {props.selectedState?.name || props.country}
      </Dropdown.Toggle>

      <Dropdown.Menu className="w-100">
        <Dropdown.Item
          css={`
            color: var(--dark);
          `}
          onClick={() => props.onChange()}
        >
          {props.country}
        </Dropdown.Item>
        {props.stateList.map(({ id, name }) => (
          <Dropdown.Item
            key={id}
            css={`
              color: var(--dark);
            `}
            onClick={() => props.onChange(id)}
          >
            {name}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};
